<script setup lang="ts">
/**
 * SignupAgreeGroup — 가입 동의 5종 + 전체 동의. 로컬·구글 두 경로가 공유한다.
 *
 * <b>흩어져 있던 동의를 한 곳으로 모은 블록이다</b> — 선택②(맞춤 정보) 동의가 입력 칸 사이에
 * 끼어 있으면 필수 동의들과 분리돼 스캔이 어렵고, 아직 아무것도 입력하지 않은 상태에서
 * "입력한 정보를 활용"에 동의하는 순서 역전이 생긴다.
 *
 * 🔴 **선택 항목의 기본값은 해제다**(설계 10번 §4.3 — 2026-08-13 판단 변경).
 * 기본 체크는 법적으로 허용되지만(§22 가 금지하는 것은 "동의 안 하면 서비스 거부"다) 실제 화면에서
 * 필수 4종이 모두 해제인데 선택만 켜져 있으면 **다크패턴으로 읽혀 신뢰를 깎는다**.
 * 입력률은 "몰래 켜두기"가 아니라 <b>전체 동의 버튼의 편의성</b>으로 얻는다 — 사용자가 스스로
 * 누른 전체 동의는 능동적 의사표시라 동의 근거가 오히려 더 단단하다.
 *
 * 필수 3종(age14·terms·privacy)만 가입 게이트다. 선택 2종(marketing·profileSegment)은
 * 값 그대로 기록될 뿐 미동의로 가입을 막지 않는다(개보법 §22⑤).
 */
import { computed } from 'vue';

/**
 * 5종 모두 `boolean | undefined` 를 받는다 — 로컬 경로가 vee-validate `defineField` 로 만든
 * 모델을 그대로 넘기는데, 그 타입이 초기 렌더 시점을 포함해 `undefined` 를 허용하기 때문이다.
 * 판정은 전부 "true 인가"라 `undefined` 는 미동의와 동일하게 취급된다.
 */
const age14 = defineModel<boolean | undefined>('age14', { required: true });
const terms = defineModel<boolean | undefined>('terms', { required: true });
const privacy = defineModel<boolean | undefined>('privacy', { required: true });
const marketing = defineModel<boolean | undefined>('marketing', { required: true });
/** 선택② 맞춤 정보 활용 동의 — 세그먼트 입력 블록과 짝이지만 체크는 여기 모아 둔다. */
const profileSegment = defineModel<boolean | undefined>('profileSegment', { required: true });

/** 필수 항목별 에러 문구(부모 폼 검증 결과) — 없으면 표시하지 않는다. */
defineProps<{
  errorAge14?: string;
  errorTerms?: string;
  errorPrivacy?: string;
}>();

const emit = defineEmits<{ openLegal: [kind: 'terms' | 'consent' | 'marketing'] }>();

/**
 * 전체 동의 — 5종이 모두 켜져 있을 때만 체크된 상태로 보인다.
 * 하나라도 해제되면 자동으로 풀리므로 "전체 동의인데 일부만 켜짐" 같은 거짓 표시가 없다.
 */
const allAgreed = computed({
  get: () => age14.value && terms.value && privacy.value && marketing.value && profileSegment.value,
  set: (value: boolean) => {
    age14.value = value;
    terms.value = value;
    privacy.value = value;
    marketing.value = value;
    profileSegment.value = value;
  },
});

const checkboxClass = 'mt-px h-[17px] w-[17px] flex-none accent-accent';
const errorTextClass = 'mt-1.5 text-[11.5px] leading-[1.5] text-danger';
const rowClass = 'flex items-start gap-[9px] text-[13.5px] leading-[1.5] text-body';
const linkClass = 'ml-1 text-brand underline hover:text-accent-deep';
</script>

<template>
  <div class="mb-5 mt-1">
    <!-- 전체 동의 — 선택 항목까지 한 번에 켜는 편의 장치(기본 해제). -->
    <label
      class="flex items-start gap-[9px] rounded-card border border-line bg-bg-3 px-3.5 py-3 text-[13.5px] font-bold leading-[1.5] text-brand"
    >
      <input v-model="allAgreed" type="checkbox" :class="checkboxClass" />
      <span>전체 동의</span>
    </label>

    <div class="mt-3 flex flex-col gap-[11px] px-1">
      <label :class="rowClass">
        <input v-model="age14" type="checkbox" :class="checkboxClass" />
        <span> <span class="font-bold text-brand">(필수)</span> 만 14세 이상입니다 </span>
      </label>
      <p v-if="errorAge14" :class="errorTextClass">{{ errorAge14 }}</p>

      <label :class="rowClass">
        <input v-model="terms" type="checkbox" :class="checkboxClass" />
        <span>
          <span class="font-bold text-brand">(필수)</span> 이용약관 동의
          <button type="button" :class="linkClass" @click="emit('openLegal', 'terms')">
            약관 보기
          </button>
        </span>
      </label>
      <p v-if="errorTerms" :class="errorTextClass">{{ errorTerms }}</p>

      <label :class="rowClass">
        <input v-model="privacy" type="checkbox" :class="checkboxClass" />
        <span>
          <span class="font-bold text-brand">(필수)</span> 개인정보 수집·이용 동의
          <button type="button" :class="linkClass" @click="emit('openLegal', 'consent')">
            내용 보기
          </button>
        </span>
      </label>
      <p v-if="errorPrivacy" :class="errorTextClass">{{ errorPrivacy }}</p>

      <label :class="rowClass">
        <input v-model="profileSegment" type="checkbox" :class="checkboxClass" />
        <span>
          <span class="font-bold text-muted">(선택)</span>
          맞춤 정보를 콘텐츠 제공에 활용 동의
          <button type="button" :class="linkClass" @click="emit('openLegal', 'consent')">
            내용 보기
          </button>
        </span>
      </label>

      <label :class="rowClass">
        <input v-model="marketing" type="checkbox" :class="checkboxClass" />
        <span>
          <span class="font-bold text-muted">(선택)</span> 마케팅 활용 동의
          <button type="button" :class="linkClass" @click="emit('openLegal', 'marketing')">
            내용 보기
          </button>
        </span>
      </label>
    </div>
  </div>
</template>
